import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, iso, MONTHS, DOW, monthGrid } from "@/lib/api";
import RangeTool from "@/pages/admin/RangeTool";
import QuickRates from "@/pages/admin/QuickRates";

const SOURCE = (info) => {
  if (!info) return null;
  if (info.site) return { label: "Site", cls: "text-brass" };
  if (info.airbnb && info.booking) return { label: "Airbnb + Booking", cls: "text-muted-foreground" };
  if (info.airbnb) return { label: "Airbnb", cls: "text-muted-foreground" };
  if (info.booking) return { label: "Booking", cls: "text-muted-foreground" };
  if (info.closed) return { label: "Fermé", cls: "text-muted-foreground" };
  if (info.booked) return { label: "Réservé", cls: "text-muted-foreground" };
  return null;
};

const DayButton = ({ cell, info, selected, onToggle }) => {
  const d = iso(cell);
  const src = SOURCE(info);
  return (
    <button onClick={() => onToggle(d)} data-testid={`admin-day-${d}`}
      className={[
        "flex h-20 flex-col items-start justify-between border p-2 text-left text-xs",
        selected ? "border-forest bg-white/10" : "border-white/10 hover:border-white/15",
        info?.booked ? "bg-white/5" : "",
      ].join(" ")}
      style={{ transition: "border-color .15s ease, background-color .15s ease" }}>
      <span className="font-semibold text-bone">{cell.getDate()}</span>
      <span className={`font-medium ${info?.custom ? "text-brass" : "text-muted-foreground"}`}>
        {info ? `${Math.round(info.price)}€` : "—"}
      </span>
      <span className={`text-[9px] uppercase tracking-wide ${src?.cls || "text-muted-foreground"}`}
        data-testid={`day-source-${d}`}>
        {src?.label || ""}
      </span>
    </button>
  );
};

export default function PricesTab({ gites }) {
  const editable = gites.filter((g) => !g.composite.length);
  const [slug, setSlug] = useState("chalet-6");
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState([]);
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);

  const start = iso(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const end = iso(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/prices", { params: { gite_slug: slug, start, end } })
      .then((r) => setDays(r.data.days))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [slug, start, end]);

  useEffect(() => { load(); setSelected([]); }, [load]);

  const byDate = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d])), [days]);
  const cells = monthGrid(cursor.getFullYear(), cursor.getMonth());

  const toggle = (d) => setSelected((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));

  const apply = async (payload) => {
    if (!selected.length) return toast.error("Sélectionnez au moins un jour");
    try {
      await api.put("/admin/prices", { gite_slug: slug, days: selected.map((d) => ({ date: d, ...payload })) });
      toast.success(`${selected.length} jour(s) mis à jour`);
      setSelected([]);
      setPrice("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="prices-tab">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        {editable.map((g) => (
          <button key={g.slug} onClick={() => setSlug(g.slug)} data-testid={`price-gite-${g.slug}`}
            className={`px-4 py-2 text-sm font-medium ${slug === g.slug ? "bg-brass text-ink" : "border border-white/15 text-bone"}`}>
            {g.name}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            data-testid="admin-cal-prev" className="grid h-9 w-9 place-items-center border border-white/15"><ChevronLeft size={16} /></button>
          <select value={cursor.getMonth()} data-testid="admin-cal-month"
            onChange={(e) => setCursor(new Date(cursor.getFullYear(), Number(e.target.value), 1))}
            className="border border-white/15 bg-panel px-2 py-1.5 text-base font-medium capitalize text-bone">
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select value={cursor.getFullYear()} data-testid="admin-cal-year"
            onChange={(e) => setCursor(new Date(Number(e.target.value), cursor.getMonth(), 1))}
            className="border border-white/15 bg-panel px-2 py-1.5 text-base font-medium text-bone">
            {[0, 1, 2].map((k) => {
              const y = today.getFullYear() + k;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            data-testid="admin-cal-next" className="grid h-9 w-9 place-items-center border border-white/15"><ChevronRight size={16} /></button>
        </span>
      </div>

      <div className="border border-white/10 bg-panel p-5">
        <div className="mb-2 grid grid-cols-7 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
          {DOW.map((d, i) => <span key={`dow-${i}`}>{d}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((c, i) => {
            if (!c) return <span key={`pad-${cursor.getFullYear()}-${cursor.getMonth()}-${i}`} />;
            const d = iso(c);
            return <DayButton key={d} cell={c} info={byDate[d]} selected={selected.includes(d)} onToggle={toggle} />;
          })}
        </div>
        {loading && <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={13} className="animate-spin" /> chargement…</p>}
        <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[10px] uppercase tracking-widest" data-testid="calendar-legend">
          <span className="text-brass">Site — réservation directe</span>
          <span className="text-muted-foreground">Airbnb — importé</span>
          <span className="text-muted-foreground">Booking — importé</span>
          <span className="text-muted-foreground">Fermé — bloqué par vous</span>
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-4 border border-white/10 bg-panel p-5">
        <div>
          <p className="text-xs text-muted-foreground">Jours sélectionnés</p>
          <p className="font-display text-2xl text-bone" data-testid="selected-count">{selected.length}</p>
        </div>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs font-medium text-bone">Prix par nuit (€)</span>
          <input type="number" min="0" step="1" className="field !py-2 w-40" data-testid="price-input"
            value={price} onChange={(e) => setPrice(e.target.value)} placeholder="ex : 165" />
        </label>
        <button className="btn-primary !py-2.5" data-testid="apply-price-btn"
          onClick={() => apply({ price: Number(price), closed: false })} disabled={!price || !selected.length}>
          Appliquer le prix
        </button>
        <button className="btn-ghost !py-2.5" data-testid="close-days-btn" onClick={() => apply({ price: null, closed: true })}>
          Bloquer ces dates
        </button>
        <button className="btn-ghost !py-2.5" data-testid="reset-days-btn" onClick={() => apply({ price: null, closed: false })}>
          Réinitialiser
        </button>
      </div>

      <QuickRates slug={slug} onDone={load} />
      <RangeTool slug={slug} onDone={load} />
    </div>
  );
}
